import { useState } from 'react';
import type { Artifact, ArtifactVendor, DemandProductStance, DemandVendorFriction } from '../types.ts';
import {
  describeActiveFilters,
  filterVendorItems,
  vendorListItemKey,
  vendorListItems,
  visibleVendors,
  type FilterState,
  type VendorListItem,
} from '../lib/filters.ts';
import { MentionList } from './MentionList.tsx';
import { Highlight } from './Highlight.tsx';
import { CitationBadges } from './CitationBadges.tsx';
import {
  featureKey,
  frictionQuoteCount,
  productStances,
  stanceCounts,
  vendorFriction,
  voicesLabel,
  type AnchoredFriction,
} from '../lib/demand.ts';
import { DemandQuoteList } from './DemandQuoteList.tsx';

/**
 * The vendor-centric pivot (spec v1.1): per vendor, ONE unified searchable feature list —
 * compared and not-compared together, plus the totality invariant's proposed/dropped residue —
 * instead of the axis-centric matrix. Filters (search/verdict/vendor/facet) are the same
 * FilterState the matrix pivot uses, so switching pivots preserves them for free.
 *
 * Vendor friction from the demand panel lands here (design-system.md, "Demand annotations"):
 * friction anchored to a feature or subfeature shows as a count tag on that row and as quotes
 * inside its expansion. What people said about a product as a whole is its own row at the head
 * of the vendor's list (design-system.md, "Product-level stance row"); below it, any anchor that
 * names nothing in this vendor's record — and, on a panel written before the stance rows
 * existed, its `entry: null` product-level friction — sits in the unplaced block. With no demand
 * block there is no friction to index and none of it draws.
 */
export function VendorExplorer({ artifact, filters }: { artifact: Artifact; filters: FilterState }): JSX.Element {
  const vendors = visibleVendors(artifact, filters);

  if (!vendors.length) {
    return <p className="panel muted">nothing matches: the vendor filter excludes every vendor</p>;
  }

  return (
    <div className="vendor-explorer">
      {vendors.map((vendor) => (
        <VendorList key={vendor.slug} vendor={vendor} artifact={artifact} filters={filters} />
      ))}
    </div>
  );
}

function VendorList({
  vendor,
  artifact,
  filters,
}: {
  vendor: ArtifactVendor;
  artifact: Artifact;
  filters: FilterState;
}): JSX.Element {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const items = filterVendorItems(vendor, artifact.matrix, filters);
  const total = vendorListItems(vendor).length;
  const expandableKeys = items.filter((i) => i.kind === 'feature').map(vendorListItemKey);
  const friction = vendorFriction(artifact.demand, vendor);
  const stances = productStances(artifact.demand, vendor);

  return (
    <section className="panel vendor-list">
      <header className="vendor-list-header">
        <h4>{vendor.company}</h4>
        <span className="vendor-meta">
          {items.length === total ? `${total} items` : `${items.length} of ${total} items`}
        </span>
        <span className="vendor-meta">
          {vendor.coverage.pages_read}/{vendor.coverage.pages_known} pages read
        </span>
        {expandableKeys.length > 0 && (
          <div className="expand-all-row">
            <button onClick={() => setExpanded(new Set(expandableKeys))}>Expand all</button>
            <button onClick={() => setExpanded(new Set())}>Collapse all</button>
          </div>
        )}
      </header>

      {stances.length > 0 && <ProductStanceRows rows={stances} query={filters.query} />}

      {friction.vendorLevel.length > 0 && (
        <VendorLevelFriction entries={friction.vendorLevel} query={filters.query} />
      )}

      {items.length === 0 ? (
        <p className="empty-note muted">nothing matches: {describeActiveFilters(filters)}</p>
      ) : (
        <div className="vendor-list-body">
          {items.map((item) => {
            const key = vendorListItemKey(item);
            return (
              <VendorItemRow
                key={key}
                item={item}
                vendor={vendor}
                matrix={artifact.matrix}
                friction={item.kind === 'feature' ? friction.anchored.get(featureKey(item.feature)) ?? [] : []}
                query={filters.query}
                open={expanded.has(key)}
                onToggle={() =>
                  setExpanded((prev) => {
                    const next = new Set(prev);
                    next.has(key) ? next.delete(key) : next.add(key);
                    return next;
                  })
                }
              />
            );
          })}
        </div>
      )}
    </section>
  );
}

/**
 * The product-level stance rows: one per product, voices and stance counts in the head, quotes
 * open beneath. No toggle and no tag — there is no feature to open them under, and a stance about
 * a product as a whole is neither a gap nor a friction finding, so it takes neutral ink only.
 */
function ProductStanceRows({ rows, query }: { rows: DemandProductStance[]; query: string }): JSX.Element {
  return (
    <div className="product-stances">
      <span className="label">Product-level stances</span>
      {rows.map((row) => {
        const stances = stanceCounts(row.stances);
        return (
          <div key={`${row.company}::${row.product}`} className="product-stance">
            <p className="product-stance-head">
              <strong>
                <Highlight text={row.product} query={query} />
              </strong>
              <span className="product-stance-meta">
                {voicesLabel(row.voices ?? 0)}
                {stances.length > 0 && ` · ${stances.map((s) => `${s.stance} ${s.count}`).join(' · ')}`}
              </span>
            </p>
            <DemandQuoteList quotes={row.quotes ?? []} highlightQuery={query} />
          </div>
        );
      })}
    </div>
  );
}

/**
 * Friction with no feature to hang on: `entry: null` is a complaint about the product as a whole
 * (a panel written before 2026-09-18 — newer panels carry those as stance rows); an entry that
 * did not resolve keeps its name, marked as unplaced, because a complaint we cannot anchor is
 * still one we found.
 */
function VendorLevelFriction({
  entries,
  query,
}: {
  entries: DemandVendorFriction[];
  query: string;
}): JSX.Element {
  return (
    <div className="vendor-friction">
      <span className="label">Product-level friction</span>
      {entries.map((f, i) => (
        <div key={i} className="vendor-friction-entry">
          <p className="feature-product muted">
            {f.product}
            {f.entry && ` · ${f.parent ? `${f.parent} › ` : ''}${f.entry} (not in this vendor's feature list)`}
          </p>
          <DemandQuoteList quotes={f.quotes ?? []} highlightQuery={query} />
        </div>
      ))}
    </div>
  );
}

function FrictionQuotes({ entries, query }: { entries: AnchoredFriction[]; query: string }): JSX.Element | null {
  if (!entries.length) return null;
  return (
    <div className="feature-friction">
      <span className="label">Friction reported</span>
      <DemandQuoteList quotes={entries.flatMap((e) => e.friction.quotes ?? [])} highlightQuery={query} />
    </div>
  );
}

/** Exported for the render tests only: its open state lives in VendorList, which static markup cannot click. */
export function VendorItemRow({
  item,
  vendor,
  matrix,
  friction,
  query,
  open,
  onToggle,
}: {
  item: VendorListItem;
  vendor: ArtifactVendor;
  matrix: Artifact['matrix'];
  friction: AnchoredFriction[];
  query: string;
  open: boolean;
  onToggle: () => void;
}): JSX.Element {
  if (item.kind === 'proposed') {
    return (
      <div className="vendor-item vendor-item-proposed">
        <span className="vendor-item-tag">proposed</span>
        <strong>
          <Highlight text={item.entry.name} query={query} />
        </strong>
        <span className="muted"> {item.entry.product} — named but never described</span>
      </div>
    );
  }

  if (item.kind === 'dropped') {
    return (
      <div className="vendor-item vendor-item-dropped">
        <span className="vendor-item-tag">dropped</span>
        <strong>
          <Highlight text={item.entry.name} query={query} />
        </strong>
        <span className="muted">
          {' '}
          {item.entry.product} — <Highlight text={item.entry.reason} query={query} />
        </span>
      </div>
    );
  }

  const feature = item.feature;
  const frictionCount = frictionQuoteCount(friction);
  return (
    <div className="vendor-item">
      <button className="feature-expansion-toggle" onClick={onToggle} aria-expanded={open}>
        <span className="disclosure" aria-hidden="true">{open ? '▾' : '▸'}</span>{' '}
        <Highlight text={feature.name} query={query} />
        {!feature.compared && <span className="vendor-item-tag">not compared</span>}
        {feature.subfeatures.length > 0 && <span className="sub-count muted"> ({feature.subfeatures.length} sub)</span>}
        <CitationBadges citedBy={feature.cited_by} vendor={vendor} matrix={matrix} />
        {frictionCount > 0 && <span className="vendor-item-tag friction-tag">friction · {frictionCount}</span>}
      </button>
      {open && (
        <div className="feature-expansion-body">
          <p className="feature-product muted">{feature.product}</p>
          <MentionList mentions={feature.mentions} highlightQuery={query} />
          <FrictionQuotes entries={friction.filter((f) => f.subfeature === null)} query={query} />
          {feature.subfeatures.map((s) => (
            <div key={s.name} className="subfeature">
              <strong>
                <Highlight text={s.name} query={query} />
              </strong>
              <CitationBadges citedBy={s.cited_by} vendor={vendor} matrix={matrix} />
              <MentionList mentions={s.mentions} highlightQuery={query} />
              <FrictionQuotes entries={friction.filter((f) => f.subfeature === s.name)} query={query} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
